import { MouseEvent, useEffect, useState } from 'react';
import styled from 'styled-components';

import { UploadIcon2 } from 'app/theme/Icons/UploadIcon2';
import { breakpoint } from 'app/theme/theme';
import { fileUploader, needItSooner } from 'app/translations/dictionary';

import { UploadedFile } from '../UploadedFile';
import { FileCard } from './FileCard';
import { useAsyncDropzone } from './useAsyncDropzone';

interface FileUploaderContentProps {
  files: UploadedFile[];
  addFiles: (newFiles: File[]) => Promise<void>;
  removeFile: (fileToRemove: UploadedFile) => void;
  clearFiles: () => void;
  onSubmit: () => void;
  onFileRejection: (rejectedFileTypes: string[]) => void;
  loading: boolean;
  disabled: boolean;
  accept?: string | string[];
  children?: JSX.Element;
}

const FileDropzoneContainer = styled.div`
  position: relative;
  padding: 0.2rem 0.2rem 0.2rem 4.6rem;
  box-shadow: inset 0 0 5px rgba(0, 0, 0, 0.25);
  border-radius: 0.5rem;
  background-color: white;
  box-shadow: 1px 1px 7px rgba(0, 0, 0, 0.16);
  height: 13rem;

  ${breakpoint.up.md`
    padding: 0.2rem 0.2rem 0.2rem 5.3rem;
  `};
`;

const DeleteFilesButton = styled.button`
  border: none;
  margin: 0;
  outline: none;
  cursor: pointer;
  background-color: inherit;
  text-align: right;
  font-size: 1.2rem;
  font-weight: 600;
  color: ${({ theme }) => theme.palette.primary};
  grid-column: 1 / -1;
  padding-bottom: 1rem;
  place-self: end;

  &:hover {
    text-decoration: underline;
  }
`;

const Icon = styled.div<{ $isValue?: boolean }>`
  position: absolute;
  left: 2.7rem;
  top: 2.5rem;
  z-index: 5;
  color: ${({ theme, $isValue }) => ($isValue ? theme.palette.black : theme.palette.gray.dustyGray)};
  ${breakpoint.up.md`
    left: 3.2rem; 
  `};
`;

const Container = styled.span<{ $isLoading: boolean }>`
  font-size: 1.6rem;
  margin: 0;
  padding: 0;
  text-align: center;
  color: ${({ theme }) => theme.palette.gray.dustyGray};

  & button {
    color: ${({ theme }) => theme.palette.primary};
    text-decoration: underline;
    background-color: transparent;
    border: none;
    box-shadow: none;
    cursor: pointer;
  }

  ${breakpoint.up.md`
    font-size: 2rem;
  `}
`;

const FileDropzoneGrid = styled.div<{ $isGrid: boolean }>`
  display: ${({ $isGrid }) => ($isGrid ? 'grid' : 'inline-block')};
  grid-template-columns: repeat(auto-fill, minmax(9.5rem, 1fr));
  grid-gap: 0.7rem;
  padding: 1rem 1rem 1rem ${({ $isGrid }) => ($isGrid ? 1 : 0)}rem;
  height: 13rem;
  overflow-y: auto;
`;

export function FileUploaderDropzoneContent({
  files,
  addFiles,
  accept,
  removeFile,
  clearFiles,
  onFileRejection,
  children,
}: FileUploaderContentProps): JSX.Element {
  const { getRootProps, getInputProps } = useAsyncDropzone({
    onDrop: addFiles,
    onFileRejection,
    accept,
  });
  const [disabled, setDisabled] = useState(false);

  const handleClearFiles = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    clearFiles();
  };

  useEffect(() => {
    if (files && files.length > 0) {
      setDisabled(true);
    } else setDisabled(false);
  }, [files]);

  const rootProps = getRootProps();

  return (
    <div>
      <Icon $isValue={Boolean(files && files.length)}>
        <UploadIcon2 />
      </Icon>
      <FileDropzoneContainer>
        {children}
        <FileDropzoneGrid $isGrid={Boolean(files.length)} {...rootProps} data-testid="files">
          <input {...getInputProps()} />
          {!disabled && (
            <Container $isLoading={disabled}>
              {needItSooner.upload}{' '}
              <button disabled={disabled} type="button">
                {needItSooner.browse}
              </button>
            </Container>
          )}
          {files.map((uploadedFile) => (
            <FileCard key={uploadedFile.id} onRemove={() => removeFile(uploadedFile)} uploadedFile={uploadedFile} />
          ))}
          {files.length > 0 && (
            <DeleteFilesButton onClick={handleClearFiles} type="button">
              {fileUploader.deleteAll}
            </DeleteFilesButton>
          )}
        </FileDropzoneGrid>
      </FileDropzoneContainer>
    </div>
  );
}
